import React, { useEffect, useState, Suspense } from 'react'
import { CardElement, useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { useAuthPost } from '../hooks/useAxios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/primitives';
import { useNavigate, useOutletContext, useLocation } from 'react-router-dom';
import { ADD_ONS, SUBSCRIPTIONS } from '../Pricing';
import { Card } from '../components/primitives';
import PaymentMethod from '../components/PaymentMethod';
import { catchErrorParse, ERRORS } from '../ErrorUtility';
import useTimedError from '../hooks/useTimedError';
import { useTranslation } from '../hooks/useTranslation';
import ThreeDotsLoading from '../components/primitives/ThreeDotsLoading';
import { useDatabase } from '../contexts/DatabaseContext';

export default function Subscribe() {
    const t = useTranslation();
    const { post } = useAuthPost();
    const { url } = useAuth();
    const { cardData, getCardData, clientData, portrait } = useOutletContext();
    const { createSubscriptionActionEvent } = useDatabase();

    const [useNewCard, setUseNewCard] = useState(!cardData || cardData.length === 0);

    const [subscriptionItem, setSubscriptionItem] = useState({
        addons: ADD_ONS.map((addon) => ({
            id: addon.id,
            enabled: addon.defaultEnabled,
        })).sort((a, b) => a.id - b.id),
        subscriptionType: 'base',
        bill: 'monthly',
    });
    const [amount, setAmount] = useState(999);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useTimedError();

    const stripe = useStripe();
    const elements = useElements();

    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (!subscriptionItem)
            return;
        let total = 0;
        for (let i = 0; i < subscriptionItem.addons.length; i++) {
            if (subscriptionItem.addons[i].enabled) {
                total += ADD_ONS[i].amount;
            }
        }
        total += SUBSCRIPTIONS[subscriptionItem.subscriptionType].amount;
        setAmount(total);
    }, [subscriptionItem]);

    useEffect(() => {
        if (cardData?.length > 0) {
            setUseNewCard(false);
        }
    }, [cardData]);

    
    const handleSubmit = async (event) => {
        event.preventDefault();
        try {
            setLoading(true);
            if (!stripe || !elements) {
                window.dispatchEvent(new CustomEvent('masterError', {
                    detail: {
                        message: "Unable to load Stripe, please try again later.",
                        action: "subscribe",
                        diabolical: true,
                    }
                }));
                setLoading(false);
                return;
            }
        
            let paymentMethodId = useNewCard ? null : cardData[0]?.id;
        
            if (!paymentMethodId) {
                // Using PaymentElement
                const { error: submitError } = await elements.submit();
                if (submitError) {
                    console.error('Error submitting PaymentElement:', submitError);
                    setError(submitError.message);
                    setLoading(false);
                    return;
                }
        
                const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
                    elements,
                });
        
                if (paymentMethodError) {
                    console.error('Error creating PaymentMethod:', paymentMethodError);
                    setError(paymentMethodError.message);
                    setLoading(false);
                    return;
                }
        
                paymentMethodId = paymentMethod.id;
            }

            const [responseData, error, errorCode, errorMessage] = await catchErrorParse(post, `${url}/webapp/subscriptions/inc`, {
                payment_method: paymentMethodId,
                subscriptionItems: [subscriptionItem],
            });

            if (error) {
                if (errorCode === ERRORS.STRIPE_FAILED_PAYMENT_ERROR) {
                    setError(errorMessage);
                    setLoading(false);
                    return;
                }
                window.dispatchEvent(new CustomEvent('masterError', {
                    detail: {
                        code: errorCode,
                        message: errorMessage ? errorMessage : error.message,
                        action: "subscribe",
                        diabolical: true,
                    }
                }));
                setLoading(false);
                return;
            }

            console.log('Subscription:', responseData);

            if (location.state && location.state.workspace) {
                navigate(`../register-${location.state.workspace}`);
            } else {
                navigate("/");
            }

            function getWorkspaceType() {
                if (location.state && location.state.workspace) {
                    switch (location.state.workspace) {
                        case "slack":
                            return 0;
                        case "teams":
                            return 1;
                        case "discord":
                            return 2;
                        case "mobile":
                            return 3;
                        default:
                            return null;
                    }
                }
                return null;
            }

            await createSubscriptionActionEvent("subscribe", amount, getWorkspaceType());
        } catch (error) {
            console.error('Error submitting form:', error);
            window.dispatchEvent(new CustomEvent('masterError', {
                detail: {
                    message: error.message,
                    action: "subscribe",
                    diabolical: true,
                }
            }));
            setLoading(false);
        }
    };

    return (
        <div className="floating-centered">
            <Card className='payment-card'>
                {(!stripe || !elements) ? <ThreeDotsLoading />
                :
                useNewCard ?
                    <>
                        <form onSubmit={handleSubmit}>
                            <PaymentElement />
                            <Button buttonType='submit settings' disabled={loading || !stripe} loading={loading} error={error}>
                                {error ? error : `Subscribe: ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount / 100)}`}
                            </Button>
                        </form>
                        {cardData?.length > 0 && 
                        <Button buttonType='settings' onClick={() => setUseNewCard(false)} disabled={loading}>
                            {t('Use Existing Card')}
                        </Button>}
                    </>
                    :
                    <>
                        <form onSubmit={handleSubmit}>
                            <PaymentMethod paymentMethod={cardData[0]} columnDisplay={portrait} />
                            <Button buttonType='submit settings' disabled={loading} loading={loading} error={error}>
                                {error ? error : `Subscribe: ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount / 100)}`}
                            </Button>
                        </form>
                        <Button buttonType='settings' onClick={() => setUseNewCard(true)} disabled={loading}>
                            {t('New Payment Method')}
                        </Button>
                    </>}
            </Card>
        </div>
    );
}